import { AddCircleOutline, Clear } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Stack,
  TextField,
  TextFieldProps,
} from "@mui/material";
import React from "react";
import {
  FieldValues,
  Path,
  PathValue,
  useController,
  UseControllerProps,
} from "react-hook-form";

export type ListInputProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TextFieldProps, "value" | "defaultValue">;

export function ListInput<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  ...props
}: ListInputProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const items: PathValue<T, Path<T>>[] = value || defaultValue || [];

  const handleChangeValue = (index: number, updatedValue: string) => {
    value[index] = updatedValue;
    fieldOnChange(value);
  };

  const handleAddItem = () => {
    value.push("");
    fieldOnChange(value);
  };

  const handleRemoveItem = (index: number) => {
    fieldOnChange(value.filter((_, i: number) => i !== index));
  };

  return (
    <FormControl component="fieldset" error={fieldState.invalid}>
      {label && (
        <FormLabel component="legend" required={props.required}>
          {label}
        </FormLabel>
      )}
      <Stack spacing={0.5}>
        {items.map((item, index) => (
          <Box key={index}>
            <TextField
              onChange={(e) => handleChangeValue(index, e.target.value)}
              value={item}
              size="small"
              {...field}
              {...props}
            />
            <IconButton onClick={() => handleRemoveItem(index)}>
              <Clear />
            </IconButton>
          </Box>
        ))}
      </Stack>
      <Box>
        <Button onClick={handleAddItem} startIcon={<AddCircleOutline />}>
          Ajouter
        </Button>
      </Box>
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormControl>
  );
}

export default ListInput;
